import React from "react";
import { motion } from "framer-motion";
import { Star, Coins, Zap, Flame, ArrowUpCircle, RotateCw, Map } from "lucide-react";

function StarRow({ count }) {
  return (
    <div className="flex justify-center gap-2 my-2" data-testid="result-stars">
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          initial={{ scale: 0, rotate: -30 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 0.15 * i, type: "spring", stiffness: 260 }}>
          <Star
            size={44}
            className={i < count ? "text-[#FFD600]" : "text-white/15"}
            fill={i < count ? "#FFD600" : "transparent"} />
        </motion.div>
      ))}
    </div>
  );
}

export default function ResultsOverlay({ result, accent, onReplay, onExit }) {
  if (!result) return null;
  const accPct = Math.round(result.accuracy * 100);
  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      style={{ background: "rgba(5,4,14,0.82)", backdropFilter: "blur(8px)" }}
      data-testid="results-overlay">
      <motion.div
        initial={{ scale: 0.85, y: 30 }} animate={{ scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 240, damping: 20 }}
        className="glass w-full max-w-md rounded-3xl p-6 text-center">
        <h2 className="text-3xl font-black mb-1">Round Complete!</h2>
        {result.leveled_up && (
          <div className="inline-flex items-center gap-1.5 text-sm font-bold text-[#FFD600] mb-1"
               data-testid="result-levelup">
            <ArrowUpCircle size={16} /> Level Up! Now Level {result.new_level}
          </div>
        )}
        <StarRow count={result.stars} />

        <div className="font-math text-5xl font-extrabold my-3" style={{ color: accent }}
             data-testid="result-accuracy">
          {accPct}%
        </div>
        <div className="text-[#A8B2D1] text-sm mb-4">
          {result.correct} / {result.total} correct
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <Stat icon={Zap} color="#00E5FF" label="XP" value={`+${result.xp_earned}`} tid="result-xp" />
          <Stat icon={Coins} color="#FFD600" label="Coins" value={`+${result.coins_earned}`} tid="result-coins" />
          <Stat icon={Flame} color="#FF7A00" label="Streak" value={result.streak} tid="result-streak" />
        </div>

        {result.strengthened?.length > 0 && (
          <div className="mb-4 text-left glass rounded-2xl p-3" data-testid="result-strengthened">
            <div className="text-xs font-bold text-[#00FF66] mb-2">✦ Facts you strengthened</div>
            <div className="flex flex-wrap gap-2">
              {result.strengthened.map((f, i) => (
                <span key={i} className="font-math text-sm px-2 py-1 rounded-lg bg-[#00FF66]/15 text-[#9dffbf]">
                  {f.a}×{f.b}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <button
            data-testid="result-replay-btn"
            onClick={onReplay}
            className="flex-1 btn-3d py-3.5 text-white flex items-center justify-center gap-2"
            style={{ background: accent, boxShadow: `0 6px 0 rgba(0,0,0,0.35)` }}>
            <RotateCw size={18} /> Play Again
          </button>
          <button
            data-testid="result-exit-btn"
            onClick={onExit}
            className="flex-1 btn-3d py-3.5 glass text-white flex items-center justify-center gap-2">
            <Map size={18} /> Map
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Stat({ icon: Icon, color, label, value, tid }) {
  return (
    <div className="glass rounded-2xl py-3" data-testid={tid}>
      <Icon size={20} className="mx-auto mb-1" style={{ color }} />
      <div className="font-black text-lg leading-none">{value}</div>
      <div className="text-[10px] text-[#A8B2D1] mt-0.5">{label}</div>
    </div>
  );
}
